import type { RouteRecordRaw } from 'vue-router'

/**
 * Routes de l'application.
 *
 * `meta.requiresAuth` protège une branche entière ; `meta.role` la réserve à un rôle
 * (défense en profondeur : l'API reste l'autorité). `meta.guestOnly` renvoie un compte
 * connecté vers l'accueil (mire, inscription).
 */
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    role?: 'admin'
    guestOnly?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      { path: '', redirect: { name: 'login' } },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/auth/LoginPage.vue'),
        meta: { guestOnly: true, title: 'Connexion' },
      },
      // Auto-inscription masquée depuis le 16 octobre 2026 (SP-29, mise en staging) : la page
      // `RegisterPage.vue`, `useUser().tryRegister` et l'API restent en place. Réactiver ici et
      // rétablir les liens « Créer un compte » dans `PublicLayout.vue`, `LandingPage.vue` et
      // `LoginPage.vue`.
      // {
      //   path: 'register',
      //   name: 'register',
      //   component: () => import('@/pages/auth/RegisterPage.vue'),
      //   meta: { guestOnly: true, title: 'Créer un compte' },
      // },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
        meta: { guestOnly: true, title: 'Mot de passe oublié' },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/pages/auth/ResetPasswordPage.vue'),
        meta: { title: 'Nouveau mot de passe' },
      },
      {
        path: 'verify-email',
        name: 'verify-email',
        component: () => import('@/pages/auth/VerifyEmailPage.vue'),
        meta: { title: 'Vérification de l’adresse' },
      },
      {
        path: 'mfa',
        name: 'mfa',
        component: () => import('@/pages/auth/MfaPage.vue'),
        meta: { requiresAuth: true, title: 'Double authentification' },
      },
      {
        path: 'mfa/enroll',
        name: 'mfa-enroll',
        component: () => import('@/pages/auth/MfaEnrollPage.vue'),
        meta: { requiresAuth: true, title: 'Application d’authentification' },
      },
      {
        path: 'confirm-email-change',
        name: 'confirm-email-change',
        component: () => import('@/pages/auth/ConfirmEmailChangePage.vue'),
        meta: { title: 'Nouvelle adresse' },
      },
    ],
  },
  // Vitrine publique (SP-21) : présentation du service, boutons connexion / inscription.
  // Déclarée AVANT le cadre connecté : les deux répondent à `/`, et à score égal le
  // routeur garde le premier enregistré. Le tableau de bord vit sur `/accueil`.
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      { path: '', name: 'landing', component: () => import('@/pages/LandingPage.vue') },
      {
        path: 'confidentialite',
        name: 'privacy',
        component: () => import('@/pages/public/PrivacyPage.vue'),
        meta: { title: 'Politique de confidentialité' },
      },
      {
        path: 'mentions-legales',
        name: 'legal-notice',
        component: () => import('@/pages/public/LegalNoticePage.vue'),
        meta: { title: 'Mentions légales' },
      },
      {
        path: 'a-propos',
        name: 'about',
        component: () => import('@/pages/public/AboutPage.vue'),
        meta: { title: 'À propos' },
      },
      {
        path: 'aide',
        name: 'help',
        component: () => import('@/pages/public/HelpPage.vue'),
        meta: { title: 'Aide' },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'accueil',
        name: 'home',
        component: () => import('@/pages/HomePage.vue'),
        meta: { title: 'Accueil' },
      },
      {
        path: 'compte',
        name: 'account',
        component: () => import('@/pages/AccountPage.vue'),
        meta: { title: 'Mon compte' },
      },
      {
        path: 'compte/conjoint',
        name: 'partner-accept',
        component: () => import('@/pages/PartnerAcceptPage.vue'),
        meta: { title: 'Association des comptes' },
      },
      {
        path: 'agenda',
        name: 'agenda',
        component: () => import('@/pages/AgendaPage.vue'),
        meta: { title: 'Agenda' },
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@/pages/DocumentsPage.vue'),
        meta: { title: 'Mes documents' },
      },
      {
        path: 'messages',
        name: 'messages',
        component: () => import('@/pages/MessagesPage.vue'),
        meta: { title: 'Messages' },
      },
      {
        path: 'messages/:id',
        name: 'conversation',
        component: () => import('@/pages/MessagesPage.vue'),
        props: true,
        meta: { title: 'Messages' },
      },
      {
        path: 'communaute',
        name: 'community',
        component: () => import('@/pages/CommunityPage.vue'),
        meta: { title: 'Communauté' },
      },
      {
        path: 'discussions',
        name: 'feeds',
        component: () => import('@/pages/FeedsPage.vue'),
        meta: { title: 'Discussions' },
      },
      // « Ressources » masqué pour l'instant (décision MOA du 2 octobre 2026) : la page
      // `ResourcesPage.vue` et l'API restent en place. Réactiver ici et dans `AppLayout.vue`.
      // {
      //   path: 'ressources',
      //   name: 'resources',
      //   component: () => import('@/pages/ResourcesPage.vue'),
      //   meta: { title: 'Ressources' },
      // },
      {
        path: 'profil',
        name: 'profile',
        component: () => import('@/pages/ProfilePage.vue'),
        meta: { title: 'Mon profil public' },
      },
      {
        path: 'profils/:userId',
        name: 'public-profile',
        component: () => import('@/pages/PublicProfilePage.vue'),
        props: true,
        meta: { title: 'Profil' },
      },
    ],
  },
  // Backoffice (SP-19) : web seulement, rôle `admin`, cadre à part. L'API exige en plus un
  // second facteur prouvé sur chaque route ; la modale de renforcement s'ouvre au besoin.
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, role: 'admin' },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: () => import('@/pages/admin/DashboardPage.vue'),
        meta: { title: 'Backoffice' },
      },
      {
        path: 'utilisateurs',
        name: 'admin-users',
        component: () => import('@/pages/admin/UsersPage.vue'),
        meta: { title: 'Utilisateurs' },
      },
      {
        path: 'signalements',
        name: 'admin-reports',
        component: () => import('@/pages/admin/ReportsPage.vue'),
        meta: { title: 'Signalements' },
      },
      {
        path: 'ressources',
        name: 'admin-resources',
        component: () => import('@/pages/admin/ResourcesPage.vue'),
        meta: { title: 'Ressources' },
      },
      {
        path: 'types-evenements',
        name: 'admin-event-types',
        component: () => import('@/pages/admin/EventTypesPage.vue'),
        meta: { title: 'Types d’événements' },
      },
      {
        path: 'abonnements',
        name: 'admin-subscriptions',
        component: () => import('@/pages/admin/SubscriptionsPage.vue'),
        meta: { title: 'Abonnements' },
      },
      {
        path: 'suppressions',
        name: 'admin-deletion-requests',
        component: () => import('@/pages/admin/DeletionRequestsPage.vue'),
        meta: { title: 'Suppressions de compte' },
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: () => import('@/pages/admin/AuditPage.vue'),
        meta: { title: 'Journal d’audit' },
      },
    ],
  },
  { path: '/:catchAll(.*)*', redirect: { name: 'home' } },
]

export default routes
